import React, { useEffect, useRef, useState } from 'react';

import Grid from '@material-ui/core/Grid';
import Slider from '@material-ui/core/Slider';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import TuneIcon from '@material-ui/icons/Tune';
import { useField } from '@unform/core';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Styled components
const Container = styled.div`
  width: ${(props) => props.width || '250px'};
`;

const StyledTextField = styled(TextField)`
  width: ${(props) => props.inputwidth || '60px'};
`;

export default function InputSlider({
  name,
  label,
  width,
  inputWidth,
  labelSize,
  labelVariant,
  icon: IconComponent,
  showIcon,
  ...rest
}) {
  const inputRef = useRef(null);
  const valueRef = useRef(100);
  const { fieldName, registerField, defaultValue = 100, error } = useField(
    name
  );

  const [value, setValue] = useState(Number(defaultValue) || 0);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => valueRef.current,
      setValue: (_, newValue) => setValue(newValue),
      clearValue: () => setValue(0),
    });
  }, [fieldName, registerField]);

  const handleSliderChange = (_, newValue) => {
    setValue(newValue);
  };

  const handleInputChange = (event) => {
    const inputValue = event.target.value;
    setValue(inputValue === '' ? '' : Number(inputValue));
  };

  const handleBlur = () => {
    if (value === '' || value < 0) setValue(0);
    else if (value > 100) setValue(100);
  };

  return (
    <Container width={width}>
      <Typography
        id="input-slider"
        gutterBottom
        variant={labelVariant}
        style={labelSize ? { fontSize: labelSize } : undefined}
      >
        {label}
      </Typography>
      <Grid container spacing={2} alignItems="center">
        {showIcon && IconComponent && (
          <Grid item>
            <IconComponent />
          </Grid>
        )}
        <Grid item xs>
          <Slider
            value={typeof value === 'number' ? value : 0}
            onChange={handleSliderChange}
            aria-labelledby="input-slider"
            min={0}
            max={100}
            {...rest}
          />
        </Grid>
        <Grid item>
          <StyledTextField
            inputRef={inputRef}
            margin="dense"
            value={value}
            onChange={handleInputChange}
            onBlur={handleBlur}
            error={!!error}
            helperText={error}
            inputProps={{
              step: 10,
              min: 0,
              max: 100,
              type: 'number',
              'aria-labelledby': 'input-slider',
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            inputwidth={inputWidth}
            {...rest}
          />
        </Grid>
      </Grid>
    </Container>
  );
}

// Prop validation
InputSlider.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  width: PropTypes.string,
  inputWidth: PropTypes.string,
  labelSize: PropTypes.string,
  labelVariant: PropTypes.string,
  icon: PropTypes.elementType,
  showIcon: PropTypes.bool,
};

InputSlider.defaultProps = {
  label: 'Percentual',
  width: '250px',
  inputWidth: '60px',
  labelSize: undefined,
  labelVariant: 'body2',
  icon: TuneIcon,
  showIcon: true,
};
